import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  Fragment,
} from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  Squares2X2Icon,
  ArrowRightStartOnRectangleIcon,
  UserIcon,
  UserPlusIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import { getUnreadNotificationsCount } from '../../redux/actions/notifications';
import { getBrandEvents } from '../../redux/actions/bookings';
import { canPayBooking } from 'utils/bookingActionability';
import { logout } from '../../redux/actions/auth';

import { useModal } from '../../context/ModalContext';
import { useAlert } from 'context/AlertContext';
import useLanguage from '../../hooks/useLanguage';

import ProfileButton from 'components/ProfileButton';
import LanguageDropdown from 'components/LanguageDropdown';
import useEasypayGate from 'hooks/useEasypayGate';
import { EasypayLockedHint } from 'components/EasypayPayout';

import logo from './nome.svg';

const NAV_ITEMS = [
  {
    to: '/listings?searchType=events',
    match: '/listings',
    searchTypeMatch: 'events',
    labelKey: 'header.events',
  },
  {
    to: '/listings?searchType=brands',
    match: '/listings',
    searchTypeMatch: 'brands',
    labelKey: 'header.brands',
  },
  { to: '/purpose', match: '/purpose', labelKey: 'purpose.title' },
  { to: '/contact-us', match: '/contact-us', labelKey: 'contact_us.title' },
];

const Header = ({ sticky = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showLoginModal } = useModal();
  const { language, setLanguage } = useLanguage();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { user, isAuthenticated } = useSelector((s) => s.auth);
  // The brand / promoter name; the email is only a fallback for sessions
  // stored before the name was part of the user payload.
  const displayName =
    user?.username || user?.name || user?.email?.split('@')[0];
  const notificationCount = useSelector(
    (s) => s.notifications.unreadCount ?? 0
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getUnreadNotificationsCount());
  }, [dispatch, isAuthenticated, user?.id]);

  // Brand pending payments (T2B-393): approved bookings still inside their
  // payment window. canPayBooking already excludes paid and expired ones.
  const isBrand = isAuthenticated && user?.role === 'brand';
  const brandEvents = useSelector((s) => s.bookings?.brandEvents || []);

  useEffect(() => {
    if (isBrand) dispatch(getBrandEvents());
  }, [dispatch, isBrand, user?.id]);

  const pendingPayEvents = useMemo(
    () => (isBrand ? brandEvents.filter((ev) => canPayBooking(ev?.booking)) : []),
    [isBrand, brandEvents]
  );

  const goToPay = useCallback(() => {
    if (pendingPayEvents.length === 1) {
      navigate(
        `/dashboard?tab=my-events&openEventManager=1&eventId=${pendingPayEvents[0].id}&emTab=booking-status`
      );
    } else {
      navigate('/dashboard?tab=my-events');
    }
  }, [navigate, pendingPayEvents]);

  useEffect(() => {
    if (!sticky) return;
    // Hysteresis: shrink only after scrolling past SHRINK_AT, and grow back
    // only once we're near the top again (< GROW_AT). The dead zone between
    // the two thresholds prevents flicker from jitter around a single line.
    const SHRINK_AT = 64;
    const GROW_AT = 8;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        const y = window.scrollY;
        setScrolled((prev) => (prev ? y > GROW_AT : y > SHRINK_AT));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [sticky]);

  const withBust = (url, v) =>
    url
      ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(v ?? '')}`
      : url;

  const avatarSrc = withBust(user?.avatar, user?.avatarUpdatedAt);

  const isNavActive = useCallback(
    (item) => {
      if (item.match !== location.pathname) return false;
      if (!item.searchTypeMatch) return true;
      const sp = new URLSearchParams(location.search);
      return sp.get('searchType') === item.searchTypeMatch;
    },
    [location.pathname, location.search]
  );

  const handleLogout = () => {
    dispatch(logout());
    showAlert({
      type: 'success',
      title: t('alert_dialog.logout.title'),
      message: t('alert_dialog.logout.success'),
      primaryAction: { label: t('common.ok') },
    });
    navigate('/');
  };

  // T2B-423: a promoter who isn't connected to Easypay can't create events. The
  // button stays visible and dimmed rather than disappearing — a control that
  // vanishes reads as a bug, and its tooltip is where the explanation lives.
  const { blocked: easypayBlocked } = useEasypayGate();

  const goToPayouts = () => navigate('/dashboard?tab=payment-details');

  const goToCreate = () => navigate('/create-event');
  const goHome = () => navigate('/');

  return (
    <nav
      className={[
        'z-40 w-full transition-all',
        sticky ? 'sticky top-0' : '',
        scrolled
          ? 'bg-white/85 backdrop-blur-md shadow-[0_4px_18px_-12px_rgba(0,0,0,0.18)]'
          : 'bg-white',
        'border-b border-black/5',
      ].join(' ')}
    >
      <div
        className={[
          'mx-auto flex items-center gap-3 xl:gap-8 px-4 sm:px-6 lg:px-8',
          scrolled ? 'py-2.5' : 'py-3 lg:py-4',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={goHome}
          aria-label="there2b — home"
          className="shrink-0 -m-2 p-2 inline-flex items-center"
        >
          <img
            src={logo}
            alt="there2b"
            className={[
              'block w-auto transition-all',
              scrolled ? 'h-9' : 'h-10 lg:h-12',
            ].join(' ')}
          />
        </button>

        <ul
          className={[
            'hidden lg:flex items-center',
            scrolled ? 'gap-3 xl:gap-5' : 'gap-3 xl:gap-7',
          ].join(' ')}
        >
          {NAV_ITEMS.map((item) => {
            const active = isNavActive(item);
            return (
              <li key={item.to} className="relative">
                <Link
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={[
                    'relative py-2 transition-colors font-medium whitespace-nowrap',
                    scrolled ? 'text-[15px]' : 'text-[15px] xl:text-base',
                    active
                      ? 'text-black-01'
                      : 'text-gray-500 hover:text-black-01',
                  ].join(' ')}
                >
                  {t(item.labelKey)}
                  {active && (
                    <span
                      aria-hidden
                      className="absolute left-0 right-0 -bottom-0.5 h-0.5 rounded bg-yellow-03"
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="hidden lg:flex items-center gap-2 ml-auto min-w-0">
          {isAuthenticated ? (
            <>
              {user?.role === 'promoter' && (
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={goToCreate}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap bg-yellow-03 hover:bg-yellow-05 text-white"
                  >
                    <PlusIcon className="h-4 w-4" strokeWidth={2.4} />
                    {t('event_create.title')}
                  </button>
                  {easypayBlocked && <EasypayLockedHint />}
                </span>
              )}

              {pendingPayEvents.length > 0 && (
                <button
                  type="button"
                  onClick={goToPay}
                  title={
                    pendingPayEvents.length === 1
                      ? t('header.pending_payment_single', {
                          event: pendingPayEvents[0]?.name || '',
                        })
                      : t('header.pending_payment_multiple', {
                          count: pendingPayEvents.length,
                        })
                  }
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 text-sm font-semibold transition whitespace-nowrap"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  {pendingPayEvents.length === 1
                    ? t('header.payment_pending_one')
                    : t('header.payments_pending', {
                        count: pendingPayEvents.length,
                      })}
                </button>
              )}

              <ProfileButton
                avatarUrl={avatarSrc}
                name={displayName}
                notificationCount={notificationCount}
                onLogout={handleLogout}
              />
              <span aria-hidden className="h-6 w-px bg-black/10 mx-1" />
              <LanguageDropdown />
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={showLoginModal}
                className="px-3.5 py-2 rounded-lg text-sm font-semibold text-black-01 hover:bg-black/5 transition"
              >
                {t('header.login')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="px-3.5 py-2 rounded-lg bg-yellow-03 hover:bg-yellow-05 text-white text-sm font-semibold transition"
              >
                {t('header.register')}
              </button>
              <span aria-hidden className="h-6 w-px bg-black/10 mx-1" />
              <LanguageDropdown />
            </>
          )}
        </div>

        <div className="lg:hidden ml-auto flex items-center">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label={t('header.open_menu') || 'Open menu'}
            className="w-9 h-9 rounded-full border border-black/5 grid place-items-center hover:bg-gray-50 relative"
          >
            <Bars3Icon className="h-[18px] w-[18px] text-black-01" />
            {isAuthenticated && notificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white shadow-[0_0_8px_rgba(239,68,68,0.7)]" />
            )}
          </button>
        </div>
      </div>

      {/* T2B-423: a standing reminder while Easypay is unfinished. Not
          dismissible on purpose — until there2b has their account id the
          promoter can only save drafts, and nothing they build reaches a brand.
          It disappears the moment the account id arrives. */}
      {easypayBlocked && (
        <div className="border-t border-brand-deep/15 bg-brand-soft">
          <div className="mx-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 sm:px-6 lg:px-8">
            <ExclamationCircleIcon
              className="h-4 w-4 shrink-0 text-brand-deep"
              strokeWidth={2}
            />
            <span className="text-[13px] font-semibold text-ink-2">
              {t('easypay.header_banner')}
            </span>
            <button
              type="button"
              onClick={goToPayouts}
              className="text-[13px] font-bold text-ink underline underline-offset-4 hover:text-brand-deep"
            >
              {t('easypay.header_banner_cta')}
            </button>
          </div>
        </div>
      )}

      <Transition show={mobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setMobileMenuOpen}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </TransitionChild>

          <div className="fixed inset-0 flex justify-end">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="w-80 max-w-[85vw] bg-white h-full shadow-2xl flex flex-col">
                {/* Top bar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
                  <img src={logo} alt="there2b" className="h-7 w-auto" />
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                    aria-label={t('header.close_menu') || 'Close menu'}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* User card */}
                {isAuthenticated && (
                  <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-br from-brand-softer to-white border-b border-black/5">
                    {avatarSrc ? (
                      <img
                        key={avatarSrc}
                        src={avatarSrc}
                        alt=""
                        className="h-11 w-11 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span
                        aria-hidden
                        className="h-11 w-11 rounded-full grid place-items-center text-sm font-bold text-[#4a3a16] shrink-0"
                        style={{
                          background:
                            'linear-gradient(135deg,#FFE7B8,#FFBD59)',
                        }}
                      >
                        {(displayName || '?')
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-muted mt-0.5">
                        {t('header.welcome_back', 'Welcome back')}
                      </p>
                    </div>
                  </div>
                )}

                <nav className="flex-1 overflow-y-auto px-3 py-3">
                  {/* Primary nav */}
                  <p className="px-2 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">
                    {t('header.discover', 'Discover')}
                  </p>
                  <div className="space-y-0.5">
                    {NAV_ITEMS.map((item) => {
                      const active = isNavActive(item);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setMobileMenuOpen(false)}
                          className={[
                            'block rounded-xl px-3 py-2.5 text-sm font-medium transition',
                            active
                              ? 'bg-brand-soft text-ink'
                              : 'text-ink-2 hover:bg-brand-softer',
                          ].join(' ')}
                        >
                          {t(item.labelKey)}
                        </Link>
                      );
                    })}
                  </div>

                  {/* Account */}
                  <p className="px-2 pt-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">
                    {t('header.account', 'Account')}
                  </p>

                  <div className="space-y-0.5">
                    {user?.role === 'promoter' && (
                      <button
                        type="button"
                        onClick={() => {
                          goToCreate();
                          setMobileMenuOpen(false);
                        }}
                        className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition text-ink-2 hover:bg-brand-softer"
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                          <PlusIcon className="h-5 w-5" strokeWidth={2} />
                        </span>
                        <span className="flex-1">
                          {t('event_create.title')}
                          {easypayBlocked && (
                            <span className="block text-[11.5px] font-normal text-muted">
                              {t('easypay.locked_tooltip')}
                            </span>
                          )}
                        </span>
                      </button>
                    )}

                    {pendingPayEvents.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          goToPay();
                          setMobileMenuOpen(false);
                        }}
                        className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition"
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500">
                          <CreditCardIcon className="h-5 w-5" strokeWidth={2} />
                        </span>
                        <span className="flex-1">
                          {pendingPayEvents.length === 1
                            ? t('header.payment_pending_one')
                            : t('header.payments_pending', {
                                count: pendingPayEvents.length,
                              })}
                        </span>
                        <span className="relative flex h-2 w-2 mr-1">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                        </span>
                      </button>
                    )}

                    {isAuthenticated ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            navigate('/dashboard');
                            setMobileMenuOpen(false);
                          }}
                          className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-brand-softer transition"
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                            <Squares2X2Icon
                              className="h-5 w-5"
                              strokeWidth={2}
                            />
                          </span>
                          <span className="flex-1">
                            {t('dashboard.title')}
                          </span>
                          {notificationCount > 0 && (
                            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white shadow-[0_0_8px_rgba(239,68,68,0.6)]">
                              {notificationCount}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleLogout();
                            setMobileMenuOpen(false);
                          }}
                          className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-gray-50 transition"
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-800 transition">
                            <ArrowRightStartOnRectangleIcon
                              className="h-5 w-5"
                              strokeWidth={2}
                            />
                          </span>
                          <span className="flex-1">{t('header.logout')}</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            showLoginModal();
                            setMobileMenuOpen(false);
                          }}
                          className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-brand-softer transition"
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                            <UserIcon className="h-5 w-5" strokeWidth={2} />
                          </span>
                          <span className="flex-1">{t('header.login')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            navigate('/register');
                            setMobileMenuOpen(false);
                          }}
                          className="group w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-brand-softer transition"
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                            <UserPlusIcon
                              className="h-5 w-5"
                              strokeWidth={2}
                            />
                          </span>
                          <span className="flex-1">
                            {t('header.register')}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </nav>

                {/* Footer: language */}
                <div className="border-t border-black/5 px-5 py-4 bg-paper-warm/40">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted mr-auto">
                      {t('header.language') || 'Language'}
                    </span>
                    {['en', 'pt'].map((code) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setLanguage(code)}
                        className={[
                          'px-3 py-1.5 rounded-lg text-xs font-semibold transition',
                          language === code
                            ? 'bg-yellow-03 text-white shadow-sm'
                            : 'bg-white text-gray-600 ring-1 ring-inset ring-line-strong hover:bg-paper-warm',
                        ].join(' ')}
                      >
                        {code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </nav>
  );
};

export default Header;
